import type { ClientOptions } from "#lib/api/api.js";
import { convertConfigCMYKToHex } from "#lib/generator/colors.js";
import { isLight } from "#lib/theme.js";
import { PUBLIC_URLS } from "#lib/url.js";
import type { FcGeneratorOptions } from "@dtx-company/flowcode-generator-v2";

interface ScanabilityDistanceResponse {
	resolution: [number, number];
	physical_pixel_size_mm: number;
	scan_distances: {
		focal_length_mm: number;
		distance_mm: number;
	}[];
	standard_scan_distance_mm: number;
	error?: string;
}

export interface CheckScanabilityResponse {
	passes: boolean;
	scores: {
		overall: number;
		environment: {
			indoor: number;
			megatron: number;
			outdoor: number;
		};
		perspective: {
			bottom: number;
			centered: number;
			left: number;
			right: number;
			top: number;
		};
	};
	error?: string;
	inverted: boolean;
}

export class ScanabilityClient {
	private baseUrl: string;
	private fetch: typeof globalThis.fetch;
	constructor(options?: () => ClientOptions) {
		this.baseUrl = PUBLIC_URLS.scanability || "/";
		this.fetch = options?.().fetch || globalThis.fetch;
	}

	async checkScanabilityDistance(
		config: FcGeneratorOptions,
		physicalSizeMm?: number,

		options?: {
			signal?: AbortSignal;
		},
	): Promise<ScanabilityDistanceResponse> {
		const queryParams = new URLSearchParams();

		if (physicalSizeMm !== undefined) {
			queryParams.append("physical_size_mm", physicalSizeMm.toString());
		}

		if (isInverted(config) === true) {
			queryParams.append("is_inverted", "true");
		}

		const response = await this.fetch(`${this.baseUrl}/v1/ml-qr-scanner/config_json_to_distance?${queryParams}`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(config),
			signal: options?.signal,
		});

		if (!response.ok) {
			const error = await response.json();
			throw new Error(error.detail?.[0]?.msg || "Failed to get config distance");
		}
		return response.json();
	}

	async checkScanability(
		config: FcGeneratorOptions,

		options?: {
			signal?: AbortSignal;
		},
	): Promise<CheckScanabilityResponse> {
		const queryParams = new URLSearchParams();

		if (isInverted(config) === true) {
			queryParams.append("is_inverted", "true");
		}

		const response = await this.fetch(`${this.baseUrl}/v1/ml-qr-scanner/config_json_to_scan?${queryParams}`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(config),
			signal: options?.signal,
		});

		if (!response.ok) {
			const error = await response.json();
			throw new Error(error.detail?.[0]?.msg || "Failed to scan config");
		}

		const result = await response.json();

		return {
			...result,
			inverted: isInverted(config),
		};
	}
}

function isInverted(config: FcGeneratorOptions): boolean {
	const rgb = config.cmykPicked && config.gridModuleColor ? convertConfigCMYKToHex(config.gridModuleColor) : (config.gridModuleColor ?? "#000000");

	if (isLight(rgb.split(",")[0])) {
		return true;
	}
	return false;
}
